import { useQuery } from '@tanstack/react-query'

import { api,type MaybeEnveloped } from '@/lib/api-client'
import { ImageWithSkeleton } from '@/components/ui/image-with-skeleton'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Card,CardContent,CardHeader,CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { User,Hash,Clock,ShieldAlert,X,AlertCircle } from 'lucide-react'
import { queryKeys } from '@/lib/query-keys'
import { formatInStoreTz } from '@/lib/time'
import { useOrganizationShop } from '@/hooks/use-organization-shop'

interface Adjustment {
  id?: string
  track?: string
  packageId?: string
  PackageId?: string
  Label?: string
  adjustmentType?: string
  type?: string
  reason?: string
  Reason?: string
  AdjustmentType?: string
  quantityChange?: string | number
  quantity?: string | number
  Quantity?: string | number
  uom?: string
  unitOfMeasureName?: string
  UnitOfMeasureName?: string
  createdAt?: string
  adjustmentDate?: string
  ActualDate?: string
  itemName?: string
  itemLabel?: string
  productName?: string
  PackageLabel?: string
  note?: string
  reasonCode?: string
  Note?: string
  reasonNote?: string
  adjustedByUserId?: string
  user?: string
  performedBy?: string
  quantityBefore?: string | number
  previousQuantity?: string | number
  quantityAfter?: string | number
  newQuantity?: string | number
  sourceLabel?: string
  rawData?: unknown
}

interface UserProfile {
  name?: string
  email?: string
  type?: string
  avatarUrl?: string
}

interface AdjustmentSidePanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  adjustment: Adjustment | null
}

export function AdjustmentSidePanel({ open, onOpenChange, adjustment }: AdjustmentSidePanelProps) {
  // Hooks run before the `!adjustment || !open` early return (rules-of-hooks:
  // the hook count may not change between renders); `enabled` gates the fetch.
  // Non-Metrc stores only have local adjustments, so the Metrc/Local track
  // badge is meaningless noise there and is hidden.
  const { shop } = useOrganizationShop()
  const isMetrcEnabled = shop?.isMetrcEnabled ?? true
  const userId = adjustment?.adjustedByUserId
  const userQuery = useQuery<UserProfile>({
    queryKey: queryKeys.userProfile(userId ?? ''),
    queryFn: async () => {
      const res = await api.get<MaybeEnveloped<UserProfile>>(`/users/${userId}`)
      return (res?.data ?? res) as UserProfile
    },
    enabled: !!userId && open && !!adjustment,
  })

  if (!adjustment || !open) return null

  const isMetrc = adjustment.track?.toLowerCase() === 'metrc' || !!adjustment.packageId || !!adjustment.PackageId || !!adjustment.Label
  const trackName = isMetrc ? 'Metrc' : 'Local'

  const type = adjustment.adjustmentType || adjustment.type || adjustment.reason || adjustment.Reason || adjustment.AdjustmentType || 'Adjustment'
  const qtyChange = parseFloat(String(adjustment.quantityChange || adjustment.quantity || adjustment.Quantity || '0'))
  const isPositive = qtyChange > 0

  const uom = adjustment.uom || adjustment.unitOfMeasureName || adjustment.UnitOfMeasureName || 'ea'
  const dateStr = adjustment.createdAt || adjustment.adjustmentDate || adjustment.ActualDate || new Date().toISOString()

  const itemName = adjustment.itemName || adjustment.itemLabel || adjustment.productName || adjustment.PackageLabel || adjustment.Label || 'Unknown Item'
  const reasonDetail = adjustment.reason || adjustment.Reason || adjustment.note || adjustment.reasonCode || adjustment.Note || adjustment.reasonNote
  const userFallback = adjustment.user || adjustment.performedBy || adjustment.adjustedByUserId || 'System/Unknown'

  const beforeQty = parseFloat(String(adjustment.quantityBefore || adjustment.previousQuantity || '0'))
  const afterQty = parseFloat(String(adjustment.quantityAfter || adjustment.newQuantity || '0'))
  const packageId = adjustment.sourceLabel || adjustment.packageId || adjustment.PackageId || adjustment.Label

  const userProfile = userQuery.data
  const loadingUser = userQuery.isLoading

  const displayName = userProfile?.name || userProfile?.email || userFallback
  const displayEmail = userProfile?.email
  const displayRole = userProfile?.type
  const avatarUrl = userProfile?.avatarUrl

  return (
    <Card className="w-full h-full border shadow-lg rounded-xl flex flex-col overflow-hidden animate-in slide-in-from-right-10 fade-in duration-300 relative bg-card">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2 bg-background z-10 border-b relative shrink-0">
        <div className="grid gap-0">
          <CardTitle className="text-lg font-bold tracking-tight leading-tight">Adjustment Details</CardTitle>
          <div className="flex flex-wrap items-center gap-x-2 mt-0.5">
            <span className="text-xs font-medium text-muted-foreground break-all">{itemName}</span>
            {isMetrcEnabled && (
              <Badge variant={isMetrc ? 'default' : 'secondary'} className="h-4 text-[9px] px-1 font-normal uppercase shrink-0">
                {trackName}
              </Badge>
            )}
            {packageId && (
              <span className="font-mono text-[10px] text-muted-foreground opacity-70">
                • {packageId}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 -mt-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="transition-transform duration-200"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-8 overflow-y-auto bg-background/50">
        <section className="space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">SUMMARY</h3>
          <div className="bg-muted/40 p-4 rounded-lg border space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <Hash className="w-4 h-4" /> Type
              </span>
              <Badge variant="outline" className="font-normal text-[11px] py-0.5 px-2 bg-muted/30 text-muted-foreground border-border/50">
                {(() => {
                  if (type === 'SyncReconciliation_InitialSync') return 'Initial System Sync'
                  if (type === 'WASTE') return 'Waste / Spoilage'
                  if (type === 'RECEIVING') return 'Inventory Received'
                  if (type === 'RECOUNT') return 'Physical Recount'
                  if (type === 'CORRECTION') return 'Data Correction'
                  return type
                })()}
              </Badge>
            </div>

            {reasonDetail && (
              <div className="flex justify-between items-start gap-4">
                <span className="text-sm font-medium text-muted-foreground flex items-center gap-1.5 shrink-0">
                  <AlertCircle className="w-4 h-4" /> Reason
                </span>
                <span className="text-sm font-medium text-right break-words max-w-[180px]">
                  {reasonDetail}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center pt-2 border-t">
              <span className="text-sm font-medium text-muted-foreground">Qty Change</span>
              <span className={`text-lg font-mono font-bold flex items-baseline gap-0.5 ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
                {isPositive ? '+' : ''}{qtyChange}
                <span className="text-sm text-muted-foreground font-normal">{uom}</span>
              </span>
            </div>
          </div>
        </section>

        <Separator />

        <section className="space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" /> Audit Information
          </h3>

          <div className="space-y-4">
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Date &amp; Time</span>
              <div className="text-sm flex items-center gap-2 font-medium">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span>{formatInStoreTz(dateStr, 'MMM dd, yyyy h:mm a')}</span>
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">Performed By</span>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-muted/60 border overflow-hidden flex items-center justify-center">
                  {avatarUrl ? (
                    <ImageWithSkeleton src={avatarUrl} alt={String(displayName)} className="h-full w-full object-cover" />
                  ) : (
                    <User className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {loadingUser ? 'Loading...' : String(displayName)}
                  </span>
                  {displayEmail && (
                    <span className="text-xs text-muted-foreground">{displayEmail}</span>
                  )}
                  {displayRole && (
                    <span className="text-[11px] text-muted-foreground uppercase tracking-wide">{displayRole}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">Previous Qty</span>
                <div className="text-sm font-mono flex items-baseline">
                  {beforeQty > 0 || qtyChange !== 0 ? (
                    <>
                      {beforeQty}
                      <span className="text-muted-foreground ml-0.5">{uom}</span>
                    </>
                  ) : "N/A"}
                </div>
              </div>

              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">New Qty</span>
                <div className="text-sm font-mono flex items-baseline">
                  {afterQty > 0 || qtyChange !== 0 ? (
                    <>
                      {afterQty}
                      <span className="text-muted-foreground ml-0.5">{uom}</span>
                    </>
                  ) : "N/A"}
                </div>
              </div>
            </div>
          </div>
        </section>

        {reasonDetail && (
          <>
            <Separator />
            <section className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">NOTES / REASON CODE</h3>
              <div className="bg-muted p-3 rounded-md text-sm whitespace-pre-wrap text-muted-foreground flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                {reasonDetail}
              </div>
            </section>
          </>
        )}

        {type.toLowerCase().includes('sync') && !!adjustment.rawData && (
          <>
            <Separator />
            <section className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">RECONCILIATION DATA</h3>
              <pre className="bg-slate-950 text-slate-50 p-3 rounded-md text-xs overflow-x-auto font-mono max-h-[200px] overflow-y-auto">
                {JSON.stringify(adjustment.rawData, null, 2)}
              </pre>
            </section>
          </>
        )}
      </CardContent>
    </Card>
  )
}
